import axios from 'axios'

const PUBLIC_KEY_PATH = '/security/encryption-key'
let publicKeyFailure = null
let publicKeyPromise = null

function bytesToBase64(bytes) {
  let binary = ''
  const chunkSize = 0x8000
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunkSize))
  }
  return btoa(binary)
}

function base64ToBytes(value) {
  const binary = atob(value.replace(/\s/g, ''))
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return bytes
}

function toBase64Url(value) {
  return btoa(unescape(encodeURIComponent(value)))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '')
}

function pemToDer(pem) {
  return base64ToBytes(pem.replace(/-----BEGIN PUBLIC KEY-----|-----END PUBLIC KEY-----/g, ''))
}

function assertWebCrypto() {
  if (!window.crypto || !window.crypto.subtle) {
    throw new Error('Secure request encryption requires Web Crypto and an HTTPS (or localhost) origin.')
  }
}

async function loadPublicKey(baseURL) {
  if (publicKeyFailure && Date.now() < publicKeyFailure.retryAfter) {
    throw publicKeyFailure.error
  }
  assertWebCrypto()
  if (!publicKeyPromise) {
    publicKeyPromise = axios.get((baseURL || '') + PUBLIC_KEY_PATH, { timeout: 15000 })
      .then(async response => {
        const responseBody = response.data
        if (responseBody && responseBody.code && responseBody.code !== 200) {
          throw new Error('Request encryption key unavailable (' + responseBody.code + '): ' +
            (responseBody.msg || 'backend rejected the public-key request'))
        }
        const payload = responseBody && responseBody.data ? responseBody.data : responseBody
        if (!payload || !payload.publicKey || !payload.keyId) {
          throw new Error('Request encryption key endpoint returned an invalid response. ' +
            'Restart or deploy the updated backend before using this frontend.')
        }
        publicKeyFailure = null
        const cryptoKey = await window.crypto.subtle.importKey(
          'spki',
          pemToDer(payload.publicKey),
          { name: 'RSA-OAEP', hash: 'SHA-256' },
          false,
          ['encrypt']
        )
        return { keyId: payload.keyId, cryptoKey }
      })
      .catch(error => {
        publicKeyPromise = null
        publicKeyFailure = { error, retryAfter: Date.now() + 5000 }
        throw error
      })
  }
  return publicKeyPromise
}

export function shouldEncryptRequest(config) {
  if (config.skipEncryption || config.url === PUBLIC_KEY_PATH) return false
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) return false
  if (typeof Blob !== 'undefined' && config.data instanceof Blob) return false
  if (config.data instanceof ArrayBuffer) return false
  return config.params !== undefined || config.data !== undefined
}

export async function encryptPayload(payload, baseURL) {
  const keyInfo = await loadPublicKey(baseURL)
  const aesKey = await window.crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt'])
  const rawAesKey = new Uint8Array(await window.crypto.subtle.exportKey('raw', aesKey))
  const iv = window.crypto.getRandomValues(new Uint8Array(12))
  const plaintext = new TextEncoder().encode(JSON.stringify(payload))
  const ciphertext = new Uint8Array(await window.crypto.subtle.encrypt({ name: 'AES-GCM', iv }, aesKey, plaintext))
  const wrappedKey = new Uint8Array(await window.crypto.subtle.encrypt({ name: 'RSA-OAEP' }, keyInfo.cryptoKey, rawAesKey))
  return {
    v: 1,
    keyId: keyInfo.keyId,
    key: bytesToBase64(wrappedKey),
    iv: bytesToBase64(iv),
    data: bytesToBase64(ciphertext)
  }
}

export async function encryptRequestConfig(config) {
  if (!shouldEncryptRequest(config)) return config
  if (config.params !== undefined) {
    config.__plainParams = config.params
    const encryptedParams = await encryptPayload(config.params, config.baseURL)
    config.params = { __enc: toBase64Url(JSON.stringify(encryptedParams)) }
  }
  if (config.data !== undefined) {
    config.__plainData = config.data
    config.data = await encryptPayload(config.data, config.baseURL)
    config.headers['Content-Type'] = 'application/json;charset=UTF-8'
  }
  config.headers['X-Encrypted-Payload'] = 'v1'
  return config
}

export function clearEncryptionKeyCache() {
  publicKeyPromise = null
  publicKeyFailure = null
}
